<script setup lang="ts">
import { computed } from "vue";
import { renderMarkdown } from "./renderMarkdown";

const { source = "" } = defineProps<{
  /** Raw Markdown source. Rendered to sanitized (XSS-safe) HTML. */
  source?: string | null
}>();

// Base text size/colour is left to the caller's classes so the rendered body blends with its
// surroundings; only structural/accent styling lives in this component.
const html = computed(() => renderMarkdown(source));
</script>

<template>
  <div class="vb-markdown" v-html="html" />
</template>

<style scoped lang="scss">
// Colours come from the app-wide CSS variables, which already switch with the dark theme.
.vb-markdown {
  word-break: break-word;

  :deep(> :first-child) {
    margin-top: 0;
  }

  :deep(> :last-child) {
    margin-bottom: 0;
  }

  :deep(p) {
    margin: 0 0 0.75em;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin: 1.25em 0 0.5em;
    font-weight: 600;
    line-height: 1.3;
    color: var(--text);
  }

  :deep(h1) {
    font-size: 1.125rem;
  }

  :deep(h2) {
    font-size: 1.0625rem;
  }

  :deep(h3) {
    font-size: 1rem;
  }

  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-size: 0.9375rem;
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.75em;
    padding-left: 1.5em;
  }

  :deep(ul) {
    list-style: disc;
  }

  :deep(ol) {
    list-style: decimal;
  }

  :deep(li) {
    margin: 0.25em 0;
  }

  :deep(li > ul),
  :deep(li > ol) {
    margin: 0.25em 0;
  }

  :deep(a) {
    color: var(--primary-dark);
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  :deep(a:hover) {
    color: var(--primary-darker);
  }

  :deep(a:focus-visible) {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 2px;
  }

  :deep(strong) {
    font-weight: 600;
    color: var(--text);
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(code) {
    padding: 0.125em 0.375em;
    border-radius: 0.375rem;
    background: color-mix(in srgb, var(--text) 8%, transparent);
    font-family: ui-monospace, "SF Mono", "SFMono-Regular", menlo, monospace;
    font-size: 0.85em;
  }

  :deep(pre) {
    margin: 0 0 0.75em;
    padding: 0.75em 1em;
    border-radius: 0.5rem;
    background: color-mix(in srgb, var(--text) 6%, transparent);
    overflow-x: auto;
  }

  :deep(pre code) {
    padding: 0;
    background: transparent;
    font-size: 0.85em;
  }

  :deep(blockquote) {
    margin: 0 0 0.75em;
    padding-left: 0.875em;
    border-left: 3px solid var(--input-border);
    color: var(--text-2);
  }

  :deep(hr) {
    margin: 1em 0;
    border: 0;
    border-top: 1px solid var(--input-border);
  }

  :deep(img) {
    max-width: 100%;
    border-radius: 0.5rem;
  }

  // Wide tables scroll inside themselves instead of stretching the dialog.
  :deep(table) {
    display: block;
    max-width: 100%;
    margin: 0 0 0.75em;
    overflow-x: auto;
    border-collapse: collapse;
  }

  :deep(th),
  :deep(td) {
    padding: 0.375em 0.5em;
    border: 1px solid var(--input-border);
    text-align: left;
  }
}

// Links keep a readable contrast on the dark surface.
.dark .vb-markdown {
  :deep(a) {
    color: var(--primary-light);
  }

  :deep(a:hover) {
    color: var(--primary-lighter);
  }
}
</style>
